import { getFormFieldErrorMsg } from "@tm/insurance/utils";
import { theme } from "@tm/utility/global";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import FlexComp from "../../../FlexComp/FlexComp";
import FormattedText from "../../../FormattedText/FormattedText";
import { ImageComponent } from "../../../ImageComponent/ImageComponent";
import SpanTag from "../../../TextStyles/SpanTag";
import { PrimaryContactNote } from "../../../PrimaryContactComp/PrimaryContactComp.styles";
import {
  Asterisk,
  Card,
  CountryTrigger,
  EmailFieldInput,
  FieldError,
  FieldLabel,
  FieldShell,
  FieldsStack,
  MobileRow,
  TextInput,
} from "./PrimaryContactCardLayout.styles";

type FieldProps = {
  countryCode?: string;
  errorMessage: string;
  placeholder: string;
  fieldName: string;
  isRequired: boolean;
  regex: string;
  title: string;
  value: string;
  isEnabled: boolean;
};

type PrimaryContactFormValues = {
  primaryContactData: {
    mobile: string;
    email: string;
  };
};

type CardLayoutProps = {
  primaryContact: {
    title: unknown;
    info: unknown;
    isEditable?: boolean;
    fields: {
      mobile?: FieldProps;
      email?: FieldProps;
    };
  };
  note?: { title?: unknown; info?: unknown };
  saveFormValuesOnBlur?: () => void;
  handleCountryCodeModal?: () => void;
  selectedCountry?: { countryCode?: string; countryFlag?: string };
  isLoading?: boolean;
};

/**
 * MWeb traveller primary contact — stacked grey fields (design reference).
 * Same RHF paths / validation as {@link PrimaryContactComp}.
 */
export function PrimaryContactCardLayout({
  primaryContact,
  note,
  saveFormValuesOnBlur,
  handleCountryCodeModal,
  selectedCountry,
  isLoading,
}: CardLayoutProps) {
  const { title, info, isEditable, fields } = primaryContact || {};
  const { email, mobile } = fields || {};
  const editable = isEditable !== false;
  const code = selectedCountry?.countryCode || mobile?.countryCode || "+91";

  const {
    control,
    formState: { errors },
  } = useFormContext<PrimaryContactFormValues>();

  const mobileDisabled =
    Boolean(isLoading) || !editable || mobile?.isEnabled === false;
  const emailDisabled =
    Boolean(isLoading) || !editable || email?.isEnabled === false;

  return (
    <Card>
      <FlexComp flexDirection="column" gap="2px" margin="0 0 16px 0">
        <FormattedText
          value={title as any}
          baseStyle={{
            fontSize: "18px",
            fontWeight: "900",
            color: "#000000",
            lineHeight: "24px",
          }}
        />
        <FormattedText
          value={info as any}
          baseStyle={{
            fontSize: "12px",
            fontWeight: "400",
            color: "#000000",
            lineHeight: "16px",
          }}
        />
      </FlexComp>

      <FieldsStack>
        {mobile ? (
          <div>
            <Controller
              control={control}
              name="primaryContactData.mobile"
              rules={{
                required: {
                  value: mobile.isRequired ?? true,
                  message: getFormFieldErrorMsg("mobile"),
                },
                pattern: {
                  value: new RegExp(mobile.regex || ".*"),
                  message: getFormFieldErrorMsg("mobile", mobile.errorMessage),
                },
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <FieldShell>
                  <FieldLabel htmlFor="primary-contact-mobile">
                    {mobile.title || "Mobile"}
                    {mobile.isRequired ? <Asterisk>*</Asterisk> : null}
                  </FieldLabel>
                  <MobileRow>
                    <CountryTrigger
                      type="button"
                      onClick={() =>
                        mobileDisabled ? undefined : handleCountryCodeModal?.()
                      }
                      disabled={mobileDisabled || !handleCountryCodeModal}
                      aria-label="Change country code"
                    >
                      {selectedCountry?.countryFlag ? (
                        <ImageComponent
                          width="20px"
                          height="14px"
                          src={selectedCountry.countryFlag}
                        />
                      ) : null}
                      <span>{code}</span>
                    </CountryTrigger>
                    <TextInput
                      id="primary-contact-mobile"
                      type="tel"
                      autoComplete="off"
                      placeholder={mobile.placeholder}
                      value={value ?? ""}
                      readOnly={mobileDisabled}
                      disabled={mobileDisabled}
                      onChange={onChange}
                      onBlur={() => {
                        saveFormValuesOnBlur?.();
                        onBlur();
                      }}
                    />
                  </MobileRow>
                </FieldShell>
              )}
            />
            {errors?.primaryContactData?.mobile?.message ? (
              <FieldError>
                {String(errors.primaryContactData.mobile.message)}
              </FieldError>
            ) : null}
          </div>
        ) : null}

        {email ? (
          <div>
            <Controller
              control={control}
              name="primaryContactData.email"
              rules={{
                required: {
                  value: email.isRequired ?? true,
                  message: getFormFieldErrorMsg("email"),
                },
                pattern: {
                  value: new RegExp(email.regex || ".*"),
                  message: getFormFieldErrorMsg("email", email.errorMessage),
                },
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <FieldShell>
                  <FieldLabel htmlFor="primary-contact-email">
                    {email.title || "Email ID"}
                    {email.isRequired ? <Asterisk>*</Asterisk> : null}
                  </FieldLabel>
                  <EmailFieldInput
                    id="primary-contact-email"
                    type="email"
                    autoComplete="off"
                    placeholder={email.placeholder}
                    value={value ?? ""}
                    readOnly={emailDisabled}
                    disabled={emailDisabled}
                    onChange={onChange}
                    onBlur={() => {
                      saveFormValuesOnBlur?.();
                      onBlur();
                    }}
                  />
                </FieldShell>
              )}
            />
            {errors?.primaryContactData?.email?.message ? (
              <FieldError>
                {String(errors.primaryContactData.email.message)}
              </FieldError>
            ) : null}
          </div>
        ) : null}
      </FieldsStack>

      {note ? (
        <PrimaryContactNote style={{ marginTop: 16 }}>
          <SpanTag color={theme.brownText} fontWeight="bold">
            <FormattedText value={note.title as any} />
          </SpanTag>
          <SpanTag color={theme.brownText}>
            <FormattedText value={note?.info as any} />
          </SpanTag>
        </PrimaryContactNote>
      ) : null}
    </Card>
  );
}
